import React from 'react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  setUserContext,
  clearUserContext,
  addBreadcrumb,
  captureMessage,
} from '../utils/sentry';
import { Button } from '@roger/ui-components';

const SentryTest: React.FC = () => {
  const { user, isAuthenticated } = useStore(
    useShallow(state => ({
      user: state.user,
      isAuthenticated: state.isAuthenticated,
    }))
  );

  const testUserContext = () => {
    if (user) {
      setUserContext({
        id: user.id,
        email: user.email,
        username: user.name,
      });
      addBreadcrumb('Manual user context set', 'test', { userId: user.id });
      captureMessage('User context manually set for testing', 'info');
    }
  };

  const testClearContext = () => {
    clearUserContext();
    addBreadcrumb('Manual user context cleared', 'test');
    captureMessage('User context manually cleared for testing', 'info');
  };

  const testError = () => {
    addBreadcrumb('About to throw test error', 'test');
    throw new Error('This is a test error for Sentry');
  };

  const testBreadcrumb = () => {
    addBreadcrumb('Test breadcrumb added', 'test', {
      timestamp: new Date().toISOString(),
      userAuthenticated: isAuthenticated,
    });
  };

  return (
    <div className='space-y-4'>
      <h3 className='text-lg font-semibold'>Sentry Test Panel</h3>

      <div className='space-y-2'>
        <p>
          <strong>User Status:</strong>{' '}
          {isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
        </p>
        {user && (
          <div>
            <p>
              <strong>User ID:</strong> {user.id}
            </p>
            <p>
              <strong>Email:</strong> {user.email}
            </p>
            <p>
              <strong>Name:</strong> {user.name}
            </p>
          </div>
        )}
      </div>

      <div className='space-y-2'>
        <Button onClick={testUserContext} disabled={!isAuthenticated}>
          Test Set User Context
        </Button>

        <Button onClick={testClearContext} variant='outline'>
          Test Clear User Context
        </Button>

        <Button onClick={testBreadcrumb}>Test Add Breadcrumb</Button>

        <Button onClick={testError} variant='destructive'>
          Test Error (will crash)
        </Button>
      </div>

      <div className='text-sm text-muted-foreground'>
        <p>Check your Sentry dashboard to see these test events.</p>
        <p>User context should be visible in all events when authenticated.</p>
      </div>
    </div>
  );
};

export default SentryTest;
